/**
 * Firestore implementation of DeliveryOrderRepository.
 *
 * Path: deliveries/{doId}
 *
 * Note: collection name is `deliveries` (not `deliveryOrders`) for short
 * URLs and consistency with the dashboard URL `/staff/deliveries`. The
 * entity itself is still called DeliveryOrder.
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import type { DeliveryOrder, EntityId } from '../../../types';
import type { DeliveryOrderRepository } from '../types';

const COLLECTION = 'deliveries';

export const firestoreDeliveryRepository: DeliveryOrderRepository = {
  async findById(id: EntityId): Promise<DeliveryOrder | null> {
    const snap = await getDoc(doc(db, COLLECTION, id));
    return snap.exists() ? (snap.data() as DeliveryOrder) : null;
  },

  async save(entity: DeliveryOrder): Promise<void> {
    await setDoc(doc(db, COLLECTION, entity.id), entity);
  },

  async delete(id: EntityId): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, id));
  },

  async list(): Promise<DeliveryOrder[]> {
    const snap = await getDocs(collection(db, COLLECTION));
    return snap.docs.map((d) => d.data() as DeliveryOrder);
  },

  async findByOrder(orderId: EntityId): Promise<DeliveryOrder[]> {
    const q = query(collection(db, COLLECTION), where('orderId', '==', orderId));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as DeliveryOrder);
  },

  async findScheduledBetween(startISO: string, endISO: string): Promise<DeliveryOrder[]> {
    const q = query(
      collection(db, COLLECTION),
      where('scheduledDate', '>=', startISO),
      where('scheduledDate', '<=', endISO),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as DeliveryOrder);
  },

  async findByStatus(status: DeliveryOrder['status']): Promise<DeliveryOrder[]> {
    const q = query(collection(db, COLLECTION), where('status', '==', status));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as DeliveryOrder);
  },
};
